<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';
import { ref, onMounted, onUnmounted, computed } from 'vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  text,
  items,
  hasTitle,
  hasText,
  hasItems,
  hasAnyContent,
} = useBlockSafe(props.data);

const checkIsBot = () => {
  if (process.client) return false;
  const headers = useRequestHeaders(['user-agent']);
  const ua = headers['user-agent'] || '';
  return /bot|googlebot|crawler|spider|robot|crawling|google web preview|chrome-lighthouse/i.test(ua);
};

const SPEED = 2.2;
const TITLE_SHARE = 0.25;
const containerRef = ref(null);
const isInView = ref(false);
const scrollProgress = ref(checkIsBot() ? 1 : 0);
const isMounted = ref(false);

const originalColor = backgroundColor.value === 'black' ? '#343434' : '#cdcdcd';
const animatedColor = backgroundColor.value === 'black' ? 'white' : '#000000';

const wrapWordsInHTML = (htmlString, className) => {
  if (!htmlString || typeof document === 'undefined') return htmlString;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;

  let wordIndex = 0;

  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text || !text.trim()) return node;

      const words = text.split(/(\s+)/);
      const fragment = document.createDocumentFragment();

      words.forEach((word) => {
        if (word.trim()) {
          const span = document.createElement('span');
          span.className = className;
          span.setAttribute('data-word-index', wordIndex++);
          span.textContent = word;
          fragment.appendChild(span);
        } else if (word) {
          fragment.appendChild(document.createTextNode(word));
        }
      });

      return fragment;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const clone = node.cloneNode(false);
      Array.from(node.childNodes).forEach((child) => {
        const processed = processNode(child);
        clone.appendChild(processed);
      });
      return clone;
    }

    return node.cloneNode(true);
  };

  const processed = document.createElement('div');
  Array.from(tempDiv.childNodes).forEach((child) => {
    processed.appendChild(processNode(child));
  });

  return processed.innerHTML;
};

const processedTitle = computed(() => {
  if (!isMounted.value) return title.value || '';
  return wrapWordsInHTML(title.value, 'description__title-word');
});

const processedText = computed(() => {
  if (!isMounted.value) return text.value || '';
  return wrapWordsInHTML(text.value, 'description__word');
});

const updateWordColors = () => {
  if (!containerRef.value || !isMounted.value) return;

  const fastProgress = Math.max(0, Math.min(1, scrollProgress.value * SPEED));

  const titleWords = containerRef.value.querySelectorAll(
    '.description__title-word',
  );
  const totalTitleWords = titleWords.length;

  if (totalTitleWords > 0) {
    const titleProgress = Math.max(0, Math.min(1, fastProgress / TITLE_SHARE));

    titleWords.forEach((word, index) => {
      const wordProgress = titleProgress * totalTitleWords - index;

      const isLast = index === totalTitleWords - 1;
      const shouldBeActive =
        isLast && titleProgress > 0.98 ? true : wordProgress > 0.5;

      word.style.color = shouldBeActive ? animatedColor : originalColor;
    });
  }

  const textWords = containerRef.value.querySelectorAll('.description__word');
  const totalTextWords = textWords.length;

  if (totalTextWords > 0) {
    const textProgress = Math.max(
      0,
      Math.min(1, (fastProgress - TITLE_SHARE) / (1 - TITLE_SHARE)),
    );

    textWords.forEach((word, index) => {
      const wordProgress = textProgress * totalTextWords - index;
      const isLast = index === totalTextWords - 1;
      const shouldBeActive =
        isLast && textProgress > 0.98 ? true : wordProgress > 0.5;

      word.style.color = shouldBeActive ? animatedColor : originalColor;
    });
  }
};

const handleScroll = () => {
  if (!containerRef.value) return;

  const rect = containerRef.value.getBoundingClientRect();
  const windowHeight = window.innerHeight;

  const isVisible = rect.top < windowHeight && rect.bottom > 0;
  isInView.value = isVisible;

  if (isVisible) {
    const elementHeight = rect.height;
    const startPoint = windowHeight * 0.7;
    const endPoint = windowHeight * 0.2;

    const scrollStart = Math.max(0, startPoint - rect.top);
    const scrollRange = Math.max(1, startPoint - endPoint + elementHeight);

    const progress = Math.max(0, Math.min(1, scrollStart / scrollRange));
    scrollProgress.value = progress;
    updateWordColors();
  } else {
    scrollProgress.value = 0;
    updateWordColors();
  }
};

onMounted(() => {
  isMounted.value = true;

  setTimeout(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
  }, 0);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

defineExpose({
  reset: () => {
    scrollProgress.value = 0;
    updateWordColors();
  },
  trigger: () => {
    scrollProgress.value = 1;
    updateWordColors();
  },
});
</script>

<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div
        :class="`description description--${template} description--${backgroundColor}`"
      >
        <div
          v-if="hasTitle || hasText"
          ref="containerRef"
          class="description__text"
          :class="{ 'description--in-view': isInView }"
        >
          <h2
            v-if="hasTitle"
            ref="titleRef"
            class="mb-0 description__title h2 text-center"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="processedTitle"
          ></h2>

          <div
            v-if="hasText"
            ref="textRef"
            class="description__content accent text-center"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="processedText"
          ></div>
        </div>
        <ul v-if="hasItems" class="m-0 p-0 description__parameters">
          <li
            v-for="(param, paramIndex) in items"
            :key="paramIndex"
            class="m-0 description__parameter"
            data-aos="fade-up"
            :data-aos-delay="paramIndex >= 1 ? 100 + 100 * paramIndex : 100"
          >
            <span
              class="description__parameter-title body-3-regular"
              v-html="param.title"
            ></span>
            <span
              class="description__parameter-value body-1-bold"
              :class="[backgroundColor === 'black' ? `white` : 'black']"
              v-html="param.value"
            ></span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.description {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 80px;
  @include media-breakpoint-up(sm) {
    gap: 120px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 120);
  }

  &__title {
    margin: 0;
  }
  &__text {
    display: flex;
    flex-direction: column;
    gap: 56px;
    position: relative;
    margin: 0 auto;
    @include media-breakpoint-up(sm) {
      gap: 80px;
    }
    @include media-breakpoint-up(md) {
      padding: 0;
      max-width: 784px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 80);
      @include responsive-spacing(max-width, 784);
    }
  }
  &__title-word {
    display: inline-block;
    transition: color 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  &__word {
    display: inline-block;
    transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  &__parameters {
    display: grid;
    grid-template-columns: 1fr;
    list-style: none;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  &__parameter {
    border-right: 1px solid $divider;
    border-left: 1px solid $divider;
    border-bottom: 1px solid $divider;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 16px 15px;
    &:first-child {
      border-radius: 8px 8px 0 0;
      border-top: 1px solid $divider;
    }
    &:nth-child(3n + 1) {
      border-left: 1px solid $divider;
    }
    &:last-child {
      border-radius: 0 0 8px 8px;
    }
    @include media-breakpoint-up(sm) {
      border-radius: 0;
      border-left: unset;
      padding: 24px 24px 23px;
      &:first-child,
      &:last-child {
        border-radius: 0;
      }
      &:first-child {
        border-top-left-radius: 8px;
      }
      &:nth-child(2) {
        border-top: 1px solid $divider;
      }
      &:nth-child(3) {
        border-top: 1px solid $divider;
        border-top-right-radius: 8px;
      }
      &:nth-child(2):last-child,
      &:only-child {
        border-top-right-radius: 8px;
      }
      &:last-child {
        border-right: 1px solid $divider;
        border-bottom-right-radius: 8px;
      }
      &:nth-last-child(3):nth-child(3n + 1),
      &:nth-last-child(2):nth-child(3n + 1),
      &:last-child:nth-child(3n + 1) {
        border-bottom-left-radius: 8px;
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
      @include responsive-spacing(padding-top, 24);
      @include responsive-spacing(padding-left, 24);
      @include responsive-spacing(padding-right, 24);
      @include responsive-spacing(padding-bottom, 23);
      @include responsive-spacing(border-bottom-width, 1);
      @include responsive-spacing(border-right-width, 1);
      &:first-child {
        @include responsive-spacing(border-top-left-radius, 8);
      }
      &:nth-child(2) {
        @include responsive-spacing(border-top-width, 1);
      }
      &:nth-child(3) {
        @include responsive-spacing(border-top-width, 1);
        @include responsive-spacing(border-top-right-radius, 8);
      }
      &:nth-child(2):last-child,
      &:only-child {
        @include responsive-spacing(border-top-right-radius, 8);
      }
      &:last-child {
        @include responsive-spacing(border-right-width, 1);
        @include responsive-spacing(border-bottom-right-radius, 8);
      }
      &:nth-last-child(3):nth-child(3n + 1),
      &:nth-last-child(2):nth-child(3n + 1),
      &:last-child:nth-child(3n + 1) {
        @include responsive-spacing(border-bottom-left-radius, 8);
      }
    }
    &-title {
      color: $on-surface-variant-2;
    }
  }
  &--car {
    #{$parent} {
      &__text {
        @include media-breakpoint-up(sm) {
          max-width: 784px;
          padding: 0 62px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-right, 62);
          @include responsive-spacing(padding-left, 62);
          @include responsive-spacing(max-width, 784);
        }
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .description {
    &__title-word,
    &__word {
      transition: none !important;
    }
  }
}
</style>
